import { useEffect, useRef, useState } from 'react';
import { useT } from './i18n.jsx';
import {
  fitBounds,
  lonLatToScreen,
  metersPerScreenPixel,
  niceScaleBar,
  surcoucheTransform,
  type Affine,
  type BasemapMeta,
  type LonLat,
  type Viewport,
} from './geo-view.js';
import type { GeneratedSite } from '@easy2log/simulation-engine';
import type { TractorView } from './types.js';

/**
 * Rendu de la simulation sur l'orthophoto.
 *
 * Canvas plutôt que SVG : à soixante-quatre places, trois zones et dix tracteurs
 * redessinés chaque seconde, le DOM deviendrait le goulot. Le canvas redessine
 * l'ensemble en une passe, sans réconciliation.
 */

const COLORS = {
  zoneControl: 'rgba(255, 193, 7, 0.22)',
  zoneControlStroke: 'rgba(255, 193, 7, 0.9)',
  zoneRamp: 'rgba(3, 169, 244, 0.18)',
  zoneRampStroke: 'rgba(3, 169, 244, 0.85)',
  zoneStaging: 'rgba(255, 255, 255, 0.06)',
  zoneStagingStroke: 'rgba(255, 255, 255, 0.35)',
  slotFree: 'rgba(120, 220, 140, 0.28)',
  slotFreeStroke: 'rgba(120, 220, 140, 0.7)',
  slotOccupied: 'rgba(255, 112, 67, 0.55)',
  slotOccupiedStroke: 'rgba(255, 112, 67, 0.95)',
  tractorEmpty: '#4fc3f7',
  tractorLaden: '#ffb300',
  tractorNoFix: '#9e9e9e',
  tractorOffline: '#ef5350',
} as const;

/**
 * Vue aérienne récente posée sur l'orthophoto, pour la lisibilité seule. Elle a
 * son propre géoréférencement, ajusté sur des points d'appui — d'où une affine
 * distincte de celle du fond, et une rotation résiduelle qu'il faut restituer.
 */
export interface SurcoucheMeta {
  label: string;
  affine: Affine;
  asset: string;
}

export interface MapCanvasProps {
  meta: BasemapMeta;
  site: GeneratedSite;
  tractors: TractorView[];
  occupiedSlotCodes: Set<string>;
  showSlotCodes: boolean;
  /** Opacité de la surcouche, de 0 (masquée) à 1. */
  surcoucheOpacite: number;
}

export function MapCanvas({
  meta,
  site,
  tractors,
  occupiedSlotCodes,
  showSlotCodes,
  surcoucheOpacite,
}: MapCanvasProps): JSX.Element {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const surcoucheRef = useRef<HTMLImageElement | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const [surcouche, setSurcouche] = useState<SurcoucheMeta | null>(null);
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const image = new Image();
    image.src = `/basemap/${meta.assets.display}`;
    image.onload = (): void => {
      imageRef.current = image;
      setImageReady(true);
    };
    // Une orthophoto absente ne doit pas empêcher de voir les tracteurs bouger :
    // on dessine alors les zones sur fond neutre.
    image.onerror = (): void => setImageReady(false);
  }, [meta.assets.display]);

  // La surcouche est facultative : son absence ne doit rien empêcher, et aucune
  // erreur ne remonte à l'utilisateur si le fichier n'a pas été déposé.
  useEffect(() => {
    let abandonne = false;
    void fetch('/surcouche/surcouche.json')
      .then((reponse) => (reponse.ok ? (reponse.json() as Promise<SurcoucheMeta>) : null))
      .then((descripteur) => {
        if (abandonne || descripteur === null) return;
        const image = new Image();
        image.src = `/surcouche/${descripteur.asset}`;
        image.onload = (): void => {
          surcoucheRef.current = image;
          setSurcouche(descripteur);
        };
      })
      .catch(() => undefined);
    return (): void => {
      abandonne = true;
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(container);
    return (): void => observer.disconnect();
  }, []);

  // Cadrage initial sur l'emprise du site — pas sur celle du raster, qui couvre
  // tout le port alors que l'action se déroule sur la digue fret.
  useEffect(() => {
    if (size.width === 0 || size.height === 0) return;

    const points: LonLat[] = site.zones.flatMap((zone) => zone.ring);
    if (points.length === 0) return;

    setViewport(
      fitBounds(
        meta.affine,
        {
          lonMin: Math.min(...points.map((p) => p.lon)),
          lonMax: Math.max(...points.map((p) => p.lon)),
          latMin: Math.min(...points.map((p) => p.lat)),
          latMax: Math.max(...points.map((p) => p.lat)),
        },
        size,
      ),
    );
  }, [meta.affine, site, size]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || viewport === null || size.width === 0) return;

    const ratio = window.devicePixelRatio;
    canvas.width = size.width * ratio;
    canvas.height = size.height * ratio;

    const context = canvas.getContext('2d');
    if (context === null) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, size.width, size.height);

    context.fillStyle = '#0b1a26';
    context.fillRect(0, 0, size.width, size.height);

    // Fond de plan
    const image = imageRef.current;
    if (imageReady && image !== null) {
      const topLeft = { x: viewport.offsetX, y: viewport.offsetY };
      context.drawImage(
        image,
        topLeft.x,
        topLeft.y,
        meta.image.width_px * viewport.scale,
        meta.image.height_px * viewport.scale,
      );
    }

    // Surcouche, entre le fond de plan et les zones : elle éclaire la photo, elle
    // ne doit jamais masquer ce que la simulation dessine.
    const vue = surcoucheRef.current;
    if (surcouche !== null && vue !== null && surcoucheOpacite > 0) {
      context.save();
      context.globalAlpha = surcoucheOpacite;
      context.imageSmoothingQuality = 'high';
      // `transform` compose avec la mise à l'échelle du ratio de pixels déjà
      // posée plus haut ; `setTransform` l'écraserait.
      context.transform(...surcoucheTransform(meta.affine, surcouche.affine, viewport));
      context.drawImage(vue, 0, 0);
      context.restore();
    }

    const toScreen = (point: LonLat): { x: number; y: number } =>
      lonLatToScreen(meta.affine, viewport, point);

    const drawRing = (ring: LonLat[], fill: string, stroke: string, lineWidth = 1.5): void => {
      if (ring.length < 3) return;
      context.beginPath();
      ring.forEach((point, index) => {
        const screen = toScreen(point);
        if (index === 0) context.moveTo(screen.x, screen.y);
        else context.lineTo(screen.x, screen.y);
      });
      context.closePath();
      context.fillStyle = fill;
      context.fill();
      context.strokeStyle = stroke;
      context.lineWidth = lineWidth;
      context.stroke();
    };

    for (const zone of site.zones) {
      if (zone.type === 'control')
        drawRing(zone.ring, COLORS.zoneControl, COLORS.zoneControlStroke, 2);
      else if (zone.type === 'ramp') drawRing(zone.ring, COLORS.zoneRamp, COLORS.zoneRampStroke, 2);
      else drawRing(zone.ring, COLORS.zoneStaging, COLORS.zoneStagingStroke, 1);
    }

    for (const slot of site.slots) {
      const occupied = occupiedSlotCodes.has(slot.code);
      drawRing(
        slot.ring,
        occupied ? COLORS.slotOccupied : COLORS.slotFree,
        occupied ? COLORS.slotOccupiedStroke : COLORS.slotFreeStroke,
        1,
      );

      if (showSlotCodes && viewport.scale > 0.35) {
        const center = toScreen(slot.center);
        context.fillStyle = 'rgba(255,255,255,0.85)';
        context.font = '9px system-ui, sans-serif';
        context.textAlign = 'center';
        context.fillText(slot.code, center.x, center.y + 3);
      }
    }

    // Tracteurs
    for (const tractor of tractors) {
      if (tractor.latitude === null || tractor.longitude === null) continue;
      const screen = toScreen({ lon: tractor.longitude, lat: tractor.latitude });

      const color = !tractor.networkUp
        ? COLORS.tractorOffline
        : !tractor.gnssValid
          ? COLORS.tractorNoFix
          : tractor.trailerRef !== null
            ? COLORS.tractorLaden
            : COLORS.tractorEmpty;

      context.beginPath();
      context.arc(screen.x, screen.y, 7, 0, Math.PI * 2);
      context.fillStyle = color;
      context.fill();
      context.strokeStyle = 'rgba(0,0,0,0.7)';
      context.lineWidth = 2;
      context.stroke();

      context.fillStyle = '#fff';
      context.font = 'bold 11px system-ui, sans-serif';
      context.textAlign = 'left';
      context.strokeStyle = 'rgba(0,0,0,0.85)';
      context.lineWidth = 3;
      const label = `${tractor.reference.replace('TRACTOR-', 'T')}${tractor.trailerRef !== null ? ` · ${tractor.trailerRef}` : ''}`;
      context.strokeText(label, screen.x + 11, screen.y + 4);
      context.fillText(label, screen.x + 11, screen.y + 4);
    }

    // Barre d'échelle : sans elle, on ne sait pas si on regarde vingt mètres ou
    // deux cents, et les positions perdent tout sens concret.
    const bar = niceScaleBar(metersPerScreenPixel(meta, viewport));
    const barY = size.height - 26;
    context.strokeStyle = '#fff';
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(20, barY);
    context.lineTo(20 + bar.pixels, barY);
    context.stroke();
    context.fillStyle = '#fff';
    context.font = '12px system-ui, sans-serif';
    context.textAlign = 'left';
    context.fillText(`${String(bar.meters)} m`, 20, barY - 6);
  }, [
    meta,
    site,
    tractors,
    occupiedSlotCodes,
    showSlotCodes,
    viewport,
    size,
    imageReady,
    surcouche,
    surcoucheOpacite,
  ]);

  return (
    <div ref={containerRef} className="map-container">
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      {meta.is_fictional && (
        <div className="map-warning">
          {t('Fond de plan fictif — les coordonnées ne sont pas réelles')}
        </div>
      )}
    </div>
  );
}
